import { useState, useCallback } from 'react';

import { trpc } from '@/api/tRpc';
import { getSourceTablesKey } from '@/lib/utils';
import { useEnvironmentStore } from '@/stores/index.js';
import { isEmpty, isString, isBoolean, isUndefined, isPlainObject } from '@/lib/type-guards';
import { isDeleteStrategy, DELETE_STRATEGIES, type CreateMappingRequest } from '@/types/api';
import type {
  ImportError,
  ExportOptions,
  ImportWarning,
  ImportProgress,
  BulkImportResult,
  ActiveStateConflict,
  ActiveStateResolution,
  ImportValidationResult,
} from '@/types/bulk-operations';

// Sibling relative path, not the '@/hooks' barrel — both files are re-exported from it, so the
// barrel would close a cycle back onto this module
import { invalidateMappingReads } from './useMappings.js';

interface ParsedImportData {
  filename: string;
  mappings: CreateMappingRequest[];
}

/**
 * An import file that cannot be read as mapping entries.
 */
class ImportFileError extends Error {
  /**
   * Creates an ImportFileError.
   *
   * @param { string } message The message shown to the user.
   */
  constructor(message: string) {
    super(message);
    this.name = 'ImportFileError';
  }
}

const validateMappings = (items: unknown[]): CreateMappingRequest[] => {
  return items.map((item, index) => {
    if (!isPlainObject(item)) {
      throw new ImportFileError(`Row ${index + 1}: expected an object`);
    }

    const parsed = item;

    // isEmpty alone leaves targetTable as `unknown` — it returns a plain boolean, not a type
    // predicate — so isString does the narrowing the assignment below needs.
    if (!isString(parsed.targetTable) || isEmpty(parsed.targetTable)) {
      throw new ImportFileError(`Row ${index + 1}: targetTable is required`);
    }

    // Preserve sourceTables and joinDefinitions from the imported JSON when present,
    // allowing multi-table mappings to round-trip through export/import correctly.
    // Only fall back to constructing a single-entry array when sourceTables is absent.
    const hasSourceTables = Array.isArray(parsed.sourceTables) && parsed.sourceTables.length > 0;

    if (!hasSourceTables && isEmpty(parsed.sourceTable)) {
      throw new ImportFileError(`Row ${index + 1}: sourceTable is required`);
    }

    const sourceTables = hasSourceTables
      ? (parsed.sourceTables as CreateMappingRequest['sourceTables'])
      : [{ isPrimary: true, schema: 'public', table: parsed.sourceTable as string }];

    if (
      !Array.isArray(parsed.primaryKeyColumns) ||
      isEmpty(parsed.primaryKeyColumns) ||
      !parsed.primaryKeyColumns.every((column) => isString(column) && !isEmpty(column))
    ) {
      throw new ImportFileError(`Row ${index + 1}: primaryKeyColumns must be a non-empty array of non-empty strings`);
    }

    const result: CreateMappingRequest = {
      sourceTables,
      targetTable: parsed.targetTable,
      primaryKeyColumns: parsed.primaryKeyColumns as string[],
      mappings: Array.isArray(parsed.mappings) ? parsed.mappings : [],
      // Absent stays undefined rather than becoming false. "Not specified" and "explicitly inactive"
      // decide different things — see findActiveStateConflicts — and the user resolves the difference
      // before import, so no value is invented here.
      isActive: isBoolean(parsed.isActive) ? parsed.isActive : undefined,
      description: isString(parsed.description) ? parsed.description : undefined,
    };

    if (Array.isArray(parsed.joinDefinitions) && parsed.joinDefinitions.length > 0) {
      result.joinDefinitions = parsed.joinDefinitions as CreateMappingRequest['joinDefinitions'];
    }

    // The adapter configuration travels by code; the server resolves it in the target environment
    if (isString(parsed.adapterConfigCode) && !isEmpty(parsed.adapterConfigCode.trim())) {
      result.adapterConfigCode = parsed.adapterConfigCode.trim();
    }

    // Absent stays absent, so the server keeps an existing mapping's saved value (a new mapping gets
    // `soft`); any other value stops the import instead of being dropped
    if (!isUndefined(parsed.deleteStrategy)) {
      if (!isDeleteStrategy(parsed.deleteStrategy)) {
        throw new ImportFileError(`Row ${index + 1}: deleteStrategy must be one of ${DELETE_STRATEGIES.join(', ')}`);
      }

      result.deleteStrategy = parsed.deleteStrategy;
    }

    return result;
  });
};

const parseJsonFile = async (file: File): Promise<CreateMappingRequest[]> => {
  const text = await file.text();
  const data = JSON.parse(text);

  if (Array.isArray(data)) {
    return validateMappings(data);
  }

  if (data.mappings && Array.isArray(data.mappings)) {
    return validateMappings(data.mappings);
  }

  throw new ImportFileError('Invalid JSON format. Expected an array of mappings or { mappings: [...] }');
};

/**
 * Hook to parse and validate import files (JSON format).
 *
 * @returns { object } Returns parsing utilities, progress state, and reset function.
 */
export const useParseImportFile = () => {
  const [progress, setProgress] = useState<ImportProgress>({
    errors: [],
    totalRows: 0,
    failedRows: 0,
    status: 'idle',
    processedRows: 0,
  });

  const parseFile = useCallback(async (file: File): Promise<ParsedImportData> => {
    setProgress({
      errors: [],
      totalRows: 0,
      failedRows: 0,
      processedRows: 0,
      status: 'parsing',
    });

    try {
      if (!file.name.toLowerCase().endsWith('.json')) {
        throw new ImportFileError('Unsupported file format. Please use a JSON file.');
      }

      const mappings = await parseJsonFile(file);

      setProgress({
        errors: [],
        failedRows: 0,
        status: 'idle',
        processedRows: 0,
        totalRows: mappings.length,
      });

      return {
        mappings,
        filename: file.name,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to parse file';
      setProgress((prev) => ({
        ...prev,
        status: 'failed',
        errors: [
          {
            row: 0,
            field: '',
            value: null,
            message: errorMessage,
          },
        ],
      }));
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    setProgress({
      errors: [],
      totalRows: 0,
      failedRows: 0,
      status: 'idle',
      processedRows: 0,
    });
  }, []);

  return {
    reset,
    progress,
    parseFile,
  };
};

/**
 * Derives the mapping key an imported entry would be saved under.
 * Matches the server and SQL derivations via the shared `getSourceTablesKey`.
 *
 * @param { CreateMappingRequest } mapping The imported mapping entry.
 * @returns { string } Returns the derived mapping key.
 */
const deriveMappingKey = (mapping: CreateMappingRequest): string =>
  getSourceTablesKey((mapping.sourceTables ?? []).map((entry) => entry.table));

/**
 * Builds the key that matches an import entry to a saved mapping: the mapping key plus the adapter
 * configuration code, compared ignoring case. One table set can have several mappings, one per adapter
 * configuration, so the mapping key alone does not identify a saved mapping. The server matches the same way.
 *
 * @param { string } mappingKey The mapping key (the source table set).
 * @param { string } adapterConfigCode The adapter configuration code.
 * @returns { string } Returns `${mappingKey}|${code in lower case}`.
 */
export const getImportMatchKey = (mappingKey: string, adapterConfigCode: string): string =>
  `${mappingKey}|${adapterConfigCode.toLowerCase()}`;

/**
 * Builds the match key of an import entry, or undefined when the entry has no adapter configuration code
 * and so cannot match a saved mapping.
 *
 * @param { CreateMappingRequest } mapping The imported mapping entry.
 * @returns { string | undefined } Returns the match key, or undefined without a code.
 */
const getEntryMatchKey = (mapping: CreateMappingRequest): string | undefined =>
  isUndefined(mapping.adapterConfigCode)
    ? undefined
    : getImportMatchKey(deriveMappingKey(mapping), mapping.adapterConfigCode);

/**
 * Finds imported entries whose active state disagrees with the mapping that already exists under the
 * same mapping key and adapter configuration code — either because the file omits `isActive` or because
 * it carries the opposite value.
 *
 * This is why the parsers keep absence: a bulk import that silently deactivates a mapping also removes
 * its table from the Debezium publication, which stops CDC decoding and cannot be backfilled by
 * re-activating. Entries with no existing counterpart cannot disagree with anything, so they are not
 * conflicts and keep whatever the file said.
 *
 * At most one conflict per match key: a mapping key plus a configuration code identifies one saved
 * mapping, so two entries resolving to it describe the same disagreement. Listing it twice would ask the
 * user to decide it twice and, since the resolution is keyed by match key, both copies would resolve
 * identically anyway. An entry without a code matches nothing; validation reports it.
 *
 * @param { CreateMappingRequest[] } mappings The parsed import entries.
 * @param { Map<string, boolean> } currentActiveByKey Active state of existing mappings, keyed by `getImportMatchKey`.
 * @returns { ActiveStateConflict[] } Returns one entry per disagreeing match key, in file order.
 */
export const findActiveStateConflicts = (
  mappings: CreateMappingRequest[],
  currentActiveByKey: Map<string, boolean>,
): ActiveStateConflict[] => {
  const conflicts: ActiveStateConflict[] = [];
  const seenKeys = new Set<string>();

  for (const mapping of mappings) {
    const matchKey = getEntryMatchKey(mapping);

    if (isUndefined(matchKey) || isUndefined(mapping.adapterConfigCode)) {
      continue;
    }

    const currentValue = currentActiveByKey.get(matchKey);

    if (isUndefined(currentValue) || mapping.isActive === currentValue || seenKeys.has(matchKey)) {
      continue;
    }

    // Absent against a saved `false` is a disagreement on paper only: keepCurrent gives false and
    // useFile gives `undefined ?? false`, so both resolutions land on the same value. Prompting for
    // it would bury the entries that do differ under a wall of choices that change nothing.
    if (isUndefined(mapping.isActive) && !currentValue) {
      continue;
    }

    seenKeys.add(matchKey);
    conflicts.push({
      currentValue,
      fileValue: mapping.isActive,
      mappingKey: deriveMappingKey(mapping),
      adapterConfigCode: mapping.adapterConfigCode,
    });
  }

  return conflicts;
};

/**
 * Resolves every entry's active state to an explicit boolean before import.
 *
 * Conflicting entries follow the user's choice; everything else takes the file's value, defaulting to
 * inactive only where the file described a mapping that does not exist yet. The server therefore never
 * receives an absent `isActive`, so an omitted field cannot change what CDC captures.
 *
 * @param { CreateMappingRequest[] } mappings The parsed import entries.
 * @param { ActiveStateConflict[] } conflicts The conflicts found for these entries.
 * @param { ActiveStateResolution } resolution The user's choice for how to resolve them.
 * @returns { CreateMappingRequest[] } Returns the entries with `isActive` set on every one.
 */
export const resolveActiveStates = (
  mappings: CreateMappingRequest[],
  conflicts: ActiveStateConflict[],
  resolution: ActiveStateResolution,
): CreateMappingRequest[] => {
  const currentByKey = new Map(
    conflicts.map((conflict) => [
      getImportMatchKey(conflict.mappingKey, conflict.adapterConfigCode),
      conflict.currentValue,
    ]),
  );

  return mappings.map((mapping) => {
    const matchKey = getEntryMatchKey(mapping);
    const current = isUndefined(matchKey) ? undefined : currentByKey.get(matchKey);

    if (resolution === 'keepCurrent' && current !== undefined) {
      return { ...mapping, isActive: current };
    }

    return { ...mapping, isActive: mapping.isActive ?? false };
  });
};

/**
 * Finds import entries whose adapter configuration code is not one of the retailer environment's codes,
 * compared ignoring case as the server does. An entry without a code is skipped; validation reports it.
 *
 * @param { CreateMappingRequest[] } mappings The parsed import entries.
 * @param { ReadonlySet<string> } knownCodes The environment's adapter configuration codes, in lower case.
 * @returns { ImportError[] } Returns one error per entry with an unknown code, in file order.
 */
export const findUnknownAdapterConfigCodes = (
  mappings: CreateMappingRequest[],
  knownCodes: ReadonlySet<string>,
): ImportError[] =>
  mappings.flatMap((mapping, index) =>
    isUndefined(mapping.adapterConfigCode) || knownCodes.has(mapping.adapterConfigCode.toLowerCase())
      ? []
      : [
          {
            row: index + 1,
            field: 'adapterConfigCode',
            value: mapping.adapterConfigCode,
            message: `Unknown adapter configuration code '${mapping.adapterConfigCode}'`,
          },
        ],
  );

/**
 * Finds import entries whose table set has a saved mapping with no adapter configuration and no saved
 * mapping with the entry's code. The server cannot match such an entry to the mapping with no
 * configuration, so it creates a new mapping next to it; the warning says so before Import.
 *
 * @param { CreateMappingRequest[] } mappings The parsed import entries.
 * @param { ReadonlySet<string> } unlinkedMappingKeys Mapping keys of the saved mappings with no adapter configuration.
 * @param { ReadonlyMap<string, boolean> } currentActiveByKey Saved mappings with a configuration, keyed by `getImportMatchKey`.
 * @returns { ImportWarning[] } Returns one warning per such entry, in file order.
 */
export const findEntriesBesideUnlinkedMappings = (
  mappings: CreateMappingRequest[],
  unlinkedMappingKeys: ReadonlySet<string>,
  currentActiveByKey: ReadonlyMap<string, boolean>,
): ImportWarning[] =>
  mappings.flatMap((mapping, index) => {
    const matchKey = getEntryMatchKey(mapping);
    const mappingKey = deriveMappingKey(mapping);

    if (isUndefined(matchKey) || !unlinkedMappingKeys.has(mappingKey) || currentActiveByKey.has(matchKey)) {
      return [];
    }

    return [
      {
        row: index + 1,
        message: `${mappingKey} has a saved mapping with no adapter. This entry creates a new mapping next to it. To update that mapping instead, open it and choose an adapter first.`,
      },
    ];
  });

/**
 * Hook to validate bulk import data against schema requirements.
 *
 * @returns { object } Returns validation function, results, and reset utility.
 */
export const useValidateImport = () => {
  const [validationResult, setValidationResult] = useState<ImportValidationResult | null>(null);

  const validate = useCallback(async (mappings: CreateMappingRequest[]): Promise<ImportValidationResult> => {
    const errors: ImportError[] = [];
    const warnings: Array<{
      row: number;
      message: string;
    }> = [];

    mappings.forEach((mapping, index) => {
      const row = index + 1;
      const primaryTable = mapping.sourceTables?.find((s) => s.isPrimary)?.table;

      if (!primaryTable?.trim()) {
        errors.push({
          row,
          value: primaryTable,
          field: 'sourceTables',
          message: 'Source table is required',
        });
      }

      if (!mapping.targetTable?.trim()) {
        errors.push({
          row,
          field: 'targetTable',
          value: mapping.targetTable,
          message: 'Target table is required',
        });
      }

      if (primaryTable && !/^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(primaryTable)) {
        errors.push({
          row,
          value: primaryTable,
          field: 'sourceTables',
          message: 'Invalid table name format',
        });
      }

      if (mapping.targetTable && !/^[a-zA-Z_][a-zA-Z0-9_.]*$/.test(mapping.targetTable)) {
        errors.push({
          row,
          field: 'targetTable',
          value: mapping.targetTable,
          message: 'Invalid table name format',
        });
      }

      // The server refuses a mapping without rules, and one such entry fails the whole import
      if (!mapping.mappings || mapping.mappings.length === 0) {
        errors.push({
          row,
          field: 'mappings',
          value: mapping.mappings?.length ?? 0,
          message: 'At least one rule is required',
        });
      }

      if (isUndefined(mapping.adapterConfigCode)) {
        errors.push({
          row,
          field: 'adapterConfigCode',
          value: mapping.adapterConfigCode,
          message: 'Adapter configuration code is required',
        });
      }

      // An entry without a saved counterpart cannot conflict, so it resolves to `isActive ?? false`
      // unchallenged — imported inactive, absent from the CDC publication, under a green "Import
      // Successful". Scoped to "a new mapping" deliberately: this validator sees only the file, and for
      // an entry that does already exist the outcome depends on the conflict resolution the user picks,
      // which the conflict card explains.
      if (isUndefined(mapping.isActive)) {
        warnings.push({
          row,
          message:
            'No active state specified - a new mapping will be imported as inactive and not added to the CDC publication',
        });
      }
    });

    // One table set can have several mappings, one per adapter configuration, so a duplicate is the same
    // mapping key with the same configuration code (ignoring case), as the server refuses it
    const seenMatchKeys = new Set<string>();
    mappings.forEach((m, index) => {
      const matchKey = getEntryMatchKey(m);

      if (isUndefined(matchKey) || isUndefined(m.adapterConfigCode)) {
        return;
      }

      const mappingKey = deriveMappingKey(m);

      if (seenMatchKeys.has(matchKey)) {
        errors.push({
          row: index + 1,
          value: mappingKey,
          field: 'sourceTables',
          message: `Duplicate mapping: ${mappingKey} with adapter configuration code ${m.adapterConfigCode}`,
        });
      }

      seenMatchKeys.add(matchKey);
    });

    const result: ImportValidationResult = {
      errors,
      warnings,
      totalRows: mappings.length,
      valid: errors.length === 0,
    };

    setValidationResult(result);

    return result;
  }, []);

  const reset = useCallback(() => {
    setValidationResult(null);
  }, []);

  return {
    reset,
    validate,
    validationResult,
  };
};

/**
 * Hook to manage bulk mapping imports with progress tracking.
 * Uses tRPC mutation for server communication.
 *
 * @returns { object } Returns import function, progress state, error handling, and reset utility.
 */
export const useBulkImport = () => {
  const utils = trpc.useUtils();
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [progress, setProgress] = useState<ImportProgress>({
    errors: [],
    totalRows: 0,
    failedRows: 0,
    status: 'idle',
    processedRows: 0,
  });

  const bulkImportMutation = trpc.mappings.bulkImport.useMutation({
    onSuccess: () => {
      invalidateMappingReads(utils);
      // Bulk import reconciles the Debezium publication, so the sync-status card is now stale
      utils.publicationSync.getSyncStatus.invalidate();
    },
  });

  const importMappingsAsync = useCallback(
    async ({
      mappings,
    }: {
      mappings: CreateMappingRequest[];
      options?: {
        validateOnly?: boolean;
        onConflict?: 'skip' | 'overwrite';
      };
    }): Promise<BulkImportResult> => {
      setProgress({
        errors: [],
        failedRows: 0,
        processedRows: 0,
        status: 'importing',
        totalRows: mappings.length,
      });

      try {
        const trpcResult = await bulkImportMutation.mutateAsync({ mappings });

        // Normalised once, here at the wire boundary, so BulkImportResult can declare both lists
        // non-optional and every consumer reads `.length` without a guard
        const publicationFailures = trpcResult.publicationFailures ?? [];
        const publicationRemovalFailures = trpcResult.publicationRemovalFailures ?? [];
        const routeWarnings = (trpcResult.routeWarnings ?? []).map(({ message, mappingKey, adapterConfigCode }) => ({
          message,
          mappingKey,
          adapterConfigCode,
        }));
        const shadowTableFailures = (trpcResult.shadowTableFailures ?? []).map(
          ({ error, mappingKey, adapterConfigCode }) => ({ mappingKey, message: error, adapterConfigCode }),
        );

        const importResult: BulkImportResult = {
          failed: 0,
          errors: [],
          skipped: 0,
          routeWarnings,
          publicationFailures,
          shadowTableFailures,
          publicationRemovalFailures,
          imported: trpcResult.imported,
          // ImportResult branches its whole header on this. Reporting success above an amber
          // publication card is the mixed signal on the one screen where the warning matters, so
          // either failure list flips it. `failed`/`errors` are excluded: both are hard-coded here
          // because the server returns no per-row errors, so testing them would be dead code.
          success:
            isEmpty(publicationFailures) &&
            isEmpty(publicationRemovalFailures) &&
            isEmpty(routeWarnings) &&
            isEmpty(shadowTableFailures),
        };

        setResult(importResult);
        setProgress({
          errors: [],
          failedRows: 0,
          status: 'complete',
          totalRows: mappings.length,
          processedRows: trpcResult.imported,
        });

        return importResult;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Import failed';
        setProgress({
          processedRows: 0,
          status: 'failed',
          totalRows: mappings.length,
          failedRows: mappings.length,
          errors: [{ row: 0, field: '', value: null, message: errorMessage }],
        });

        throw error;
      }
    },
    [bulkImportMutation],
  );

  const reset = useCallback(() => {
    setResult(null);
    setProgress({
      errors: [],
      totalRows: 0,
      failedRows: 0,
      status: 'idle',
      processedRows: 0,
    });
    bulkImportMutation.reset();
  }, [bulkImportMutation]);

  return {
    reset,
    result,
    progress,
    importMappingsAsync,
    error: bulkImportMutation.error,
    isError: bulkImportMutation.isError,
    isLoading: bulkImportMutation.isPending,
    isSuccess: bulkImportMutation.isSuccess,
    importMappings: (args: { mappings: CreateMappingRequest[] }) => {
      importMappingsAsync(args);
    },
  };
};

/**
 * Builds request headers with environment context from the store.
 *
 * @returns { Record<string, string> } Returns headers with retailer and environment context.
 */
const buildExportHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'X-Requested-With': 'XMLHttpRequest',
  };

  const { currentEnvironment, currentRetailerCode } = useEnvironmentStore.getState();

  if (currentRetailerCode) {
    headers['X-Retailer-ID'] = currentRetailerCode;
  }

  if (currentEnvironment) {
    headers['X-Environment'] = currentEnvironment;
  }

  return headers;
};

/**
 * Hook to manage bulk mapping exports with error handling.
 * Uses inline fetch for blob streaming (not supported by tRPC).
 *
 * @returns { object } Returns export function, loading state, error state, and reset utility.
 */
export const useBulkExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const exportMappings = useCallback(async (options: ExportOptions): Promise<void> => {
    setIsExporting(true);
    setError(null);

    try {
      const searchParams = new URLSearchParams();

      if (!options.includeInactive) {
        searchParams.set('activeOnly', 'true');
      }

      // Absent exports every mapping. An empty selection is still sent, and the server refuses it,
      // so a selection can never turn into an export of every mapping
      if (!isUndefined(options.mappingIds)) {
        searchParams.set('mappingIds', options.mappingIds.join(','));
      }

      const response = await fetch(`/api/mappings/export?${searchParams.toString()}`, {
        credentials: 'include',
        headers: buildExportHeaders(),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({
          error: { code: 'UNKNOWN', message: response.statusText },
        }));
        throw new Error(errorBody?.error?.message ?? response.statusText);
      }

      const blob = await response.blob();
      const filename = `mappings-export-${new Date().toISOString().split('T')[0]}.json`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      const err = e instanceof Error ? e : new Error('Export failed');
      setError(err);
      throw err;
    } finally {
      setIsExporting(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    reset,
    isExporting,
    exportMappings,
  };
};
